
import React, { Component, Fragment } from 'react';
import Panel from '@/components/Panel';
import { createPortal } from 'react-dom';
import { CloseOutlined, PlusCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Divider, Input, Popover, Select, Spin } from 'antd';
import { getGPIOSpecLibrary } from '../../../../services/Sierra/library';
import { GPIO_SPEC } from '../../../../constants/libraryConstants';
import { numberCheck } from '../../../../services/helper/dataProcess';
import { checkNameFormat } from '../../../../services/helper/nameFormatCheck';
import { splitValueUnit } from '../../../../services/helper/RLCValue';
import { getClockItemValue, getVoltageValue } from '../../../../services/Sierra/helper/measurementHelper';
import { CUSTOM, I2C, INTERFACE_MERGE_TYPES } from '../../../../services/PCBHelper/constants';
import { unitChange } from '../../../../services/helper/mathHelper';
import { getPanelMaxHeight } from '../../../../services/helper/panelSizeHelper';
import { VDD, CLOCK, defaultSpecConfig, getSelectValueOptions, getSpecItemTitle, getSpecItemKey, I2C_DEFAULT_SPEC, GENERIC_DEFAULT_SPEC } from '../../../../services/Sierra/GPIOSpec';
import "../sierraPanel.css"
import "./index.css"


const childrenObj = {
  [VDD]: [{ key: "vHigh", name: "V-High", defaultUnit: "%" }, { key: "vLow", name: "V-Low", defaultUnit: "%" }],
  [CLOCK]: [{ key: "rising", name: "Rising Time" }, { key: "falling", name: "Falling Time" }, { key: "setup", name: "Setup Time", enabledKey: "I2C" }, { key: "hold", name: "Hold Time", enabledKey: "I2C" }, { key: "valid", name: "Valid Time", enabledKey: "I2C" }]
}
const unitOptions = {
  [VDD]: [{ value: "", label: "V" }, { value: "m", label: "mV" }],
  vRange: [{ value: "", label: "V" }, { value: "m", label: "mV" }, { value: "%", label: "% VDD" }],
  [CLOCK]: [{ value: "", label: "Hz" }, { value: "K", label: "KHz" }, { value: "M", label: "MHz" }, { value: "G", label: "GHz" }],
  time: [{ value: "", label: "s" }, { value: "m", label: "ms" }, { value: "u", label: "us" }, { value: "n", label: "ns" }, { value: "p", label: "ps" }],
}
const popoverContent = (
  <div className='gpio-spec-help-content'>
    <div className='gpio-spec-help-title'>V-High / V-Low</div>
    <p>V-High: 70%VDD - Max</p>
    <p>V-Low: 0V - 30%VDD</p>
    <p>{`Min V-High > Max V-Low`}</p>
    <div className='gpio-spec-help-title'>Rising Time / Falling Time</div>
    <img src={'/lib/imgs/thumbnail_image.png'} style={{ width: '100%' }} alt="" />
    <div className='gpio-spec-help-title'>I2C - Setup Time / Hold Time / Valid Time</div>
    <img src={`/lib/imgs/svgImgs/i2c_setup_hold_spec.svg`} style={{ width: '100%' }} alt="" />
  </div>
)

class GPIOSpecPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      name: props.libraryName,
      config: JSON.parse(JSON.stringify(defaultSpecConfig)),
      editInputInfo: {
        index: 0,
        value: "",
        unit: "",
        key: "", // vdd/clock/rising/falling/min/max
        parentKey: "", // vHigh/vLow
        type: "", // VDD/Clock,
        openType: "", // select open or not,
      },
      error: {}, // { info: "", type: "" }
      nameError: "", // name format error info
      loading: true,
      maxHeight: 800
    }
    this.dialogRoot = document.getElementById('root');
    this.inputRef = {};
  }

  async componentDidMount() {
    const { libraryId, libraryName } = this.props;
    this.resize();
    try {
      const config = await this.getConfig(libraryId);
      this.setState({ name: libraryName, config, loading: false });
    } catch (error) {
      this.setState({ name: libraryName, config: JSON.parse(JSON.stringify(defaultSpecConfig)), loading: false });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize);
  }

  resize = () => {
    const offset = this.dialogRoot.getBoundingClientRect();
    this.setState({
      maxHeight: getPanelMaxHeight(offset, 800) - 80,
    })
  }

  async componentDidUpdate(prevProps) {
    const { libraryId, libraryName } = this.props;
    if (prevProps.libraryId !== libraryId) {
      this.setState({ loading: true }, async () => {
        const config = await this.getConfig(libraryId);
        this.setState({ name: libraryName, config, loading: false });
      })
    }
  }

  getConfig = async (libraryId) => {
    let config = JSON.parse(JSON.stringify(defaultSpecConfig));
    if (libraryId) {
      const res = await getGPIOSpecLibrary(libraryId);
      if (Object.keys(res.config).length > 0) {
        config = res.config;
        if (!config.gpioType) {
          config.gpioType = CUSTOM;
        }
        if (!config.vdds || config.vdds.length === 0) {
          config = { ...config, vdds: JSON.parse(JSON.stringify(defaultSpecConfig.vdds)) }
        }
        if (!config.clocks || config.clocks.length === 0) {
          config = { ...config, clocks: JSON.parse(JSON.stringify(defaultSpecConfig.clocks)) }
        } else {
          config.clocks = this.updateTime(config.clocks);
        }
      }
    }

    return config;
  }

  updateTime = (clocks) => {
    for (let item of clocks) {
      if (typeof (item.rising) === "object") {
        if (!item.rising.min || item.rising.min === "-") {
          const rUnit = (item.rising.max || "").slice(-1);
          item.rising.min = ["m", "u", "n", "p"].includes(rUnit) ? rUnit : ""
        }
      } else {
        const unit = (item.rising || "").slice(-1);
        item.rising = { min: item.rising && ["m", "u", "n", "p"].includes(unit) ? unit : "", max: item.rising || "" }
      }

      if (typeof (item.falling) === "object") {
        if (!item.falling.min || item.falling.min === "-") {
          const fUnit = (item.falling.max || "").slice(-1);
          item.falling.min = ["m", "u", "n", "p"].includes(fUnit) ? fUnit : ""
        }
      } else {
        const unit = (item.falling || "").slice(-1);
        item.falling = { min: item.falling && ["m", "u", "n", "p"].includes(unit) ? unit : "", max: item.falling || "" }
      }
    }
    return clocks;
  }

  closeModal = async () => {
    const { config, nameError, name } = this.state;
    if (nameError) return;

    const { libraryId, isEdit } = this.props;
    if (isEdit) {
      // Clear unnecessary data
      for (const key in config) {
        if (Array.isArray(config[key])) {
          config[key] = config[key].filter(item => item.vdd || item.clock || item.gpioType);
        }
      }
      this.props.saveLibrary({ id: libraryId, name, config, type: GPIO_SPEC })
    }

    this.props.closeLibraryPanel();
  }

  // add VDD/Clock
  addItem = (type) => {
    const { isEdit } = this.props;
    if (!isEdit) return;
    const { config } = this.state;
    const newConfig = { ...config };
    if (type === VDD) {
      const newItem = { vdd: "", vHigh: { min: "", max: "" }, vLow: { min: "", max: "" } };
      newConfig.vdds.push(newItem);
    } else {
      const newItem = { clock: "", rising: { min: "", max: "" }, falling: { min: "", max: "" }, setup: { min: "", max: "" }, hold: { min: "", max: "" } };
      if (config.gpioType === I2C) {
        newItem.valid = { min: "", max: "" };
      }
      newConfig.clocks.push(newItem);
    }
    this.setState({ config: newConfig });
  }

  // delete VDD/Clock
  deleteItem = (type, index) => {
    const { isEdit } = this.props;
    if (!isEdit) return;
    const { config } = this.state;
    const newConfig = { ...config };
    if (type === VDD) {
      newConfig.vdds.splice(index, 1);
    } else {
      newConfig.clocks.splice(index, 1);
    }
    this.setState({ config: newConfig });
  }

  // Pass in editInputInfo from the outside when you modify unit
  saveValue = (paramEditInputInfo) => {

    const { config, editInputInfo } = this.state;
    const { index, value, unit, key, parentKey, type } = paramEditInputInfo || editInputInfo;

    if (type === 'name') {
      let error = checkNameFormat(value, { reg: /[^0-9a-zA-Z_$-][^0-9a-zA-Z_$-.]+/g });
      if (!error) {
        const { libraryName: prevName, libraryList } = this.props;
        const libraryNames = libraryList.map(item => item.name);
        if (libraryNames.includes(value) && value !== prevName) {
          error = "The name already exists, please enter another name."
        }
      }
      this.setState({ name: value, editInputInfo: {}, nameError: error, openType: "" });
      return;
    }

    // Data check, must be numeric type
    if (value && !!numberCheck(value)) {
      this.setState({ editInputInfo: {}, openType: "" });
      return;
    };
    const newConfig = { ...config };
    let error = "";
    try {
      const configKey = type;
      if (parentKey === 'vHigh' || parentKey === 'vLow') { // vHigh/vLow
        if (value) {
          const vdd = newConfig[configKey][index].vdd;
          const vHighValue = newConfig[configKey][index].vHigh;
          const vLowValue = newConfig[configKey][index].vLow;
          const realValue = getVoltageValue(`${value}${unit}`, vdd);
          const vLowMin = getVoltageValue(vLowValue.min, vdd);
          const vLowMax = getVoltageValue(vLowValue.max, vdd);
          const vHighMin = getVoltageValue(vHighValue.min, vdd);
          const vHighMax = getVoltageValue(vHighValue.max, vdd);

          if (parentKey === 'vHigh') {
            if (key === 'min') {
              if (vLowMax && realValue < vLowMax) {
                error = 'The minimum value of V-High cannot be less than the maximum value of V-Low.'
              } else if (vHighMax && realValue > vHighMax) {
                error = 'V-Hig: the minimum value cannot be greater than the maximum value.'
              }
            } else if (key === 'max' && vHighMin && realValue < vHighMin) {
              error = 'V-High: the maximum value cannot be less than the minimum value.'
            }
          } else {
            if (key === 'min' && vLowMax && realValue > vLowMax) {
              error = 'V-Low: the minimum value cannot be greater than the maximum value.'
            } else if (key === 'max') {
              if (vLowMin && realValue < vLowMin) {
                error = 'V-Low: the maximum value cannot be less than the minimum value.'
              } else if (vHighMin && realValue > vHighMin) {
                error = 'The maximum value of V-Low cannot be greater than the minimum value of V-High.'
              }
            }
          }
        }

        if (!error) {
          newConfig[configKey][index][parentKey][key] = `${value}${unit}`;
        } else {
          this.setState({ editInputInfo: {}, openType: "", error: { info: error, type, index, parentKey } });
          return;
        }
      } else if (key === 'vdd' || key === 'clock') {// vdd/clock
        const isExist = newConfig[configKey].find((item, i) => item[key] === `${value}${unit}` && i !== index);
        if (isExist) {
          error = `${value}${unit}${type === VDD ? 'V' : 'Hz'} already exists, please input another value.`;
          this.setState({ editInputInfo: {}, openType: "", error: { info: error, type, index } });
          return;
        }
        newConfig[configKey][index][key] = `${value}${unit}`;
      } else if (["rising", "falling", "setup", "hold", "valid"].includes(parentKey)) { // rising/falling/setup/hold/valid
        if (value) {
          const keyValue = newConfig[configKey][index][parentKey];
          const realValue = unitChange({
            num: value,
            oldUnit: `${unit}s`,
            newUnit: "s"
          }).number;

          const { minValue, maxValue } = getClockItemValue(keyValue);//unit is s
          //check range
          const title = (childrenObj[CLOCK].find(item => item.key === parentKey) || { name: key }).name;
          if ((key === "min" && maxValue && realValue > maxValue)
            || (key === "max" && minValue && realValue < minValue)) { // min < max
            error = `${title}: the maximum value cannot be less than the minimum value.`
          }
        }

        if (!error) {
          newConfig[configKey][index][parentKey][key] = `${value}${unit}`;
        } else {
          this.setState({ editInputInfo: {}, openType: "", error: { info: error, type, index, parentKey } });
          return;
        }
      }
    } catch (error) {
      console.error(error);
    }
    this.setState({ config: newConfig, editInputInfo: {}, openType: "", error: {} });
  }

  changeEditInputInfo = (info) => {
    this.setState({ editInputInfo: { ...this.state.editInputInfo, ...info } });
  }

  inputFoucs = (editInputInfo) => {
    this.setState({ editInputInfo, openType: `${editInputInfo.key}${editInputInfo.index}` });
  }

  selectVddOrClock = (value) => {
    const { editInputInfo, config } = this.state;
    const { index, type, key } = editInputInfo;
    // It triggers the blur event bound to ref
    this.inputRef[`${editInputInfo.key}${editInputInfo.index}`].blur();
    const newConfig = { ...config };
    const configKey = type;

    const isExist = newConfig[configKey].find((item, i) => item[key] === value && i !== index);
    if (isExist) {
      const error = `${value}${type === VDD ? 'V' : 'Hz'} already exists, please input another value.`;
      this.setState({ editInputInfo: {}, openType: "", error: { info: error, type, index } });
    } else {
      const _defaultConfig = getSelectValueOptions(config.gpioType)[type].find(item => item.value === value).defaultConfig;
      newConfig[configKey][index] = JSON.parse(JSON.stringify(_defaultConfig));
      this.setState({ config: { ...newConfig }, editInputInfo: {}, openType: "", error: {} });
    }
  }

  selectGPIOType = (value) => {
    const { config } = this.state;
    let _config = { ...config };
    _config.gpioType = value;

    if (value !== I2C) {
      _config.vdds = JSON.parse(JSON.stringify([GENERIC_DEFAULT_SPEC[VDD][0].defaultConfig]));
      _config.clocks = JSON.parse(JSON.stringify([GENERIC_DEFAULT_SPEC[CLOCK].find(item => item.value === "1M").defaultConfig]));
    } else {
      _config.vdds = JSON.parse(JSON.stringify([I2C_DEFAULT_SPEC[VDD][0].defaultConfig]));
      _config.clocks = JSON.parse(JSON.stringify([I2C_DEFAULT_SPEC[CLOCK].find(item => item.value === "1M").defaultConfig]));
    }

    this.setState({ config: { ..._config } });
  }

  inputRender = ({ index, value, unit, key, parentKey, unitType, className, type }) => {
    const { isEdit } = this.props;
    const { editInputInfo, openType, config } = this.state;
    const isCurrentEdit = editInputInfo.key === key && editInputInfo.parentKey === parentKey && editInputInfo.type === type && editInputInfo.index === index;
    return <>
      <Input
        disabled={!isEdit}
        className={className}
        value={isCurrentEdit ? editInputInfo.value : value}
        onFocus={() => this.inputFoucs({ index, value, unit, key, parentKey, type })}
        onChange={(e) => this.changeEditInputInfo({ value: e.target.value })}
        onBlur={() => this.saveValue()}
        onPressEnter={() => this.saveValue()}
        addonAfter={unitType === VDD
          ? <span className='addon-after-span'>V</span>
          : <Select
            disabled={!isEdit}
            className='addon-after'
            options={unitOptions[unitType]}
            value={isCurrentEdit ? editInputInfo.unit : unit}
            onChange={(_unit) => this.saveValue({ index, value, unit: _unit, key, parentKey, type })}
          />}
        ref={(ref) => this.inputRef[`${key}${index}`] = ref}
      />
      {['vdd', 'clock'].includes(key) ? <Select
        value={value}
        open={openType === `${key}${index}`}
        className="gpio-spec-item-select-value"
        suffixIcon={null}
        onChange={this.selectVddOrClock}
        getPopupContainer={() => document.getElementById("root")}
        options={getSelectValueOptions(config.gpioType)[type]}
      /> : null}
    </>
  }

  itemChildRender = (item, key, type, index) => {
    if (type === VDD) {
      const { value: minValue, unit: minUnit } = splitValueUnit((item[key] || {}).min, true);
      const { value: maxValue, unit: maxUnit } = splitValueUnit((item[key] || {}).max, true);
      return <>
        <div className='gpio-spec-item-vdd-value'>
          {this.inputRender({ index, value: minValue, unit: minUnit, key: "min", parentKey: key, unitType: "vRange", type })}
          <svg width="25" height="1" className='line'>
            <line x1="0" y1="0.5" x2="100%" y2="0.5" stroke="black" strokeWidth="1" />
          </svg>
          {this.inputRender({ index, value: maxValue, unit: maxUnit, key: "max", parentKey: key, unitType: 'vRange', type })}
        </div>
      </>
    } else {
      const { minValue, minUnit, maxValue, maxUnit } = getClockItemValue(item[key], true);
      return <>
        <div className='gpio-spec-item-vdd-value'>
          {this.inputRender({ index, value: minValue, unit: minUnit, key: "min", parentKey: key, unitType: 'time', className: "gpio-spec-item-value", type })}
          <svg width="25" height="1" className='line'>
            <line x1="0" y1="0.5" x2="100%" y2="0.5" stroke="black" strokeWidth="1" />
          </svg>
          {this.inputRender({ index, value: maxValue, unit: maxUnit, key: "max", parentKey: key, unitType: 'time', className: "gpio-spec-item-value", type })}
        </div>
      </>
      // const { value, unit } = splitValueUnit(item[key], true);
      // return this.inputRender(index, value, unit, key, null, 'time', "gpio-spec-item-value", type)
    }
  }

  itemRender = (item, type, index, arrLength) => {
    const { error, config } = this.state;
    const itemKey = getSpecItemKey(type);
    const { value, unit } = splitValueUnit(item[itemKey], true);
    return <div key={`gpio-spec-${index}`} className='gpio-spec-box'>
      <div className='gpio-spec-item'>
        <span className='gpio-spec-item-name'>
          {getSpecItemTitle(type)}
          <PlusCircleOutlined
            className='add-icon'
            onClick={() => this.addItem(type)}
          />
        </span>
        <div className='gpio-spec-item-type-value'>
          {this.inputRender({ index, value, unit, key: itemKey, parentKey: null, unitType: type, type })}
          {arrLength > 1 ? <CloseOutlined
            className='delete-icon'
            onClick={() => this.deleteItem(type, index)}
          /> : null}
        </div>
      </div>
      {error.info && error.type === type && error.index === index && !error.parentKey ? <div className='error-tip'>{error.info}</div> : null}
      {childrenObj[type].map(({ key, name, enabledKey }) => {
        if (enabledKey && enabledKey !== config.gpioType) {
          return null;
        }
        return <Fragment key={`${key}-${index}`}>
          <div className='gpio-spec-child-item'>
            <span className='gpio-spec-item-name'>
              {name}
            </span>
            {this.itemChildRender(item, key, type, index)}
          </div>
          {error.info && error.type === type && error.index === index && error.parentKey === key ? <div className='error-tip'>{error.info}</div> : null}
        </Fragment>
      })}
    </div>
  }

  render() {
    const { title, isEdit } = this.props;
    const { config, name, editInputInfo, nameError, loading, maxHeight } = this.state;
    const { vdds = [], clocks = [] } = config || {};
    const content = (
      <Fragment>
        <Panel
          className='sierra-panel'
          position='panel-center-left'
          title={<Fragment>
            <span>{title}</span>
            <Popover placement="top" title="Default" content={popoverContent} classNames={{ root: 'gpio-spec-popover' }}>
              <QuestionCircleOutlined className='vector-help-icon' />
            </Popover>
          </Fragment>}
          zIndex={2000}
          onCancel={this.closeModal}
          width={600}
          draggable
          minHeight={200}
          maxHeight={maxHeight}
        >   <Spin tip="Loading..." spinning={loading}>
            <div className='gpio-spec-content'>
              <div className='gpio-spec-item'>
                <span className='gpio-spec-item-name'>
                  Name
                </span>
                <Input
                  disabled={!isEdit}
                  value={editInputInfo.type === 'name' ? editInputInfo.value : name}
                  className='gpio-spec-item-value'
                  onFocus={() => this.setState({ editInputInfo: { type: 'name', value: name } })}
                  onChange={(e) => this.changeEditInputInfo({ value: e.target.value })}
                  onBlur={() => this.saveValue()}
                />
              </div>
              <div className='gpio-spec-item'>
                <span className='gpio-spec-item-name'>
                  GPIO Type
                </span>
                <Select
                  disabled={!isEdit}
                  value={config.gpioType || CUSTOM}
                  className='gpio-spec-item-value'
                  onChange={(value) => this.selectGPIOType(value)}
                  options={INTERFACE_MERGE_TYPES.map(item => ({ value: item, label: item }))}
                />
              </div>
              {nameError ? <div className='error-tip'>{nameError}</div> : null}

              {vdds.map((item, index) => this.itemRender(item, VDD, index, vdds.length))}
              <Divider className="gpio-spec-divider" />
              {clocks.map((item, index) => this.itemRender(item, CLOCK, index, clocks.length))}
            </div>
          </Spin>
        </Panel>
      </Fragment >
    )
    return createPortal(content, this.dialogRoot)
  }
}

export default GPIOSpecPanel;